// MA-395 (suppressed-skill-ids-cloud-sync slice) — one-shot reconnect retry
// for a guarded suppressedSkillIds push. Mirrors levelsPushRetry.ts's
// pushLevelsWithRetry exactly (same "one reconnect mechanism, reused at both
// call sites" rationale, and the same SIMPLE payload shape — no cloudMirror,
// unlike assignedSkillsPushRetry.ts, because suppressed_skill_ids has no
// second writer to reconcile against).
//
// updateSuppressedSkillIdsSafe is safe to retry with the identical payload
// any number of times: an identical re-push either fails the same guard
// again (fine, no-op) or succeeds once (fine). On failure this registers a
// single registerOnlineCallback retry of the SAME payload and unregisters
// itself once that retry fires — it only covers "still in this tab,
// reconnects soon"; a later app boot re-deriving "local still newer"
// (App/suppressedSkillsPullMerge.js) is the durable fallback for a tab that
// closes before reconnecting. Rejects with the original error so callers
// keep their own error UI/telemetry.

import { updateSuppressedSkillIdsSafe } from "@/supabaseClient.js";
import { registerOnlineCallback } from "@/lib/reachability";

export interface SuppressedSkillsPushPayload {
  suppressedSkillIds: string[];
  suppressedSkillIdsUpdatedAt: string;
}

export interface SuppressedSkillsPushDeps {
  updateSuppressedSkills: typeof updateSuppressedSkillIdsSafe;
  onReconnect: typeof registerOnlineCallback;
}

const defaultDeps: SuppressedSkillsPushDeps = {
  updateSuppressedSkills: updateSuppressedSkillIdsSafe,
  onReconnect: registerOnlineCallback,
};

export function pushSuppressedSkillsWithRetry(
  studentId: string,
  payload: SuppressedSkillsPushPayload,
  deps: SuppressedSkillsPushDeps = defaultDeps,
): Promise<unknown> {
  return deps.updateSuppressedSkills(studentId, payload).catch((err: unknown) => {
    const unregister = deps.onReconnect(() => {
      unregister();
      void deps.updateSuppressedSkills(studentId, payload).catch(() => {});
    });
    throw err;
  });
}
